import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Check, ArrowRight, Loader2, AlertCircle, Mail, Star, CreditCard, RotateCcw, XCircle, Lock } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";

const STATUS_LABEL = {
  incomplete: { fr: "Non abonné", color: "text-[#A8A39D]" },
  trial: { fr: "Essai", color: "text-[#C5A059]" },
  active: { fr: "Actif", color: "text-[#C5A059]" },
  cancelling: { fr: "Actif — annulation prévue", color: "text-orange-400" },
  past_due: { fr: "Paiement en retard", color: "text-orange-400" },
  suspended: { fr: "Suspendu", color: "text-red-400" },
  cancelled: { fr: "Annulé", color: "text-red-400" },
};

export default function Billing() {
  const { user, refresh } = useAuth();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [err, setErr] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [devVerifyToken, setDevVerifyToken] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const pollRef = useRef(null);

  // Load Stripe publishable key once, on mount
  useEffect(() => {
    api.get("/billing/config").then(({ data }) => {
      if (data.publishable_key) {
        setStripePromise(loadStripe(data.publishable_key));
      }
    }).catch(() => {});
  }, []);

  const fetchClientSecret = useCallback(async () => {
    if (!selectedPlan) return null;
    const { data } = await api.post("/billing/checkout", {
      plan_id: selectedPlan,
      origin_url: window.location.origin,
    });
    return data.client_secret;
  }, [selectedPlan]);

  const loadAll = async () => {
    const [p, s, sub] = await Promise.all([api.get("/billing/plans"), api.get("/billing/status"), api.get("/billing/subscription").catch(() => ({ data: {} }))]);
    setPlans(p.data); setStatus({ ...s.data, ...sub.data });
  };

  useEffect(() => {
    loadAll().catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Consume the "intent_plan" stored at registration: when the user lands here
  // after email verification, auto-prepare the checkout for that plan.
  useEffect(() => {
    if (!user || !user.email_verified) return;
    const intent = localStorage.getItem("cafespot_intent_plan");
    if (!intent) return;
    if (status?.subscription_status === "active") {
      localStorage.removeItem("cafespot_intent_plan");
      return;
    }
    if (status && status.subscription_status !== "active" && !selectedPlan && !loading) {
      localStorage.removeItem("cafespot_intent_plan");
      checkout(intent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, status]);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sessionId = search.get("session_id");
    const sStatus = search.get("status");
    if (sessionId && sStatus === "success" && !polling) {
      setPolling(true);
      let attempts = 0;
      const maxAttempts = 12;
      pollRef.current = setInterval(async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
          setPollMsg(`Statut paiement : ${data.payment_status || "…"} (essai ${attempts})`);
          if (data.payment_status === "paid" || data.status === "complete") {
            clearInterval(pollRef.current);
            setPolling(false);
            setPollMsg("Paiement confirmé. Abonnement actif.");
            await refresh();
            await loadAll();
            setTimeout(() => nav("/dashboard/billing", { replace: true }), 1500);
            return;
          }
          if (data.status === "expired" || attempts >= maxAttempts) {
            clearInterval(pollRef.current);
            setPolling(false);
            setPollMsg("Le paiement n'a pas pu être confirmé. Veuillez réessayer.");
          }
        } catch (e) {
          if (attempts >= maxAttempts) {
            clearInterval(pollRef.current);
            setPolling(false);
            setPollMsg("Vérification expirée.");
          }
        }
      }, 2500);
    }
    if (sStatus === "cancel") {
      setPollMsg("Paiement annulé. Vous pouvez choisir un plan à nouveau.");
    }
    // eslint-disable-next-line
  }, [search]);

  const checkout = async (planId) => {
    setErr(""); setClientSecret("");
    setSelectedPlan(planId);
    // Scroll to the embedded checkout container after render
    setTimeout(() => {
      const el = document.querySelector('[data-testid="embedded-checkout-container"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
  };

  const cancelEmbeddedCheckout = () => {
    setSelectedPlan(null);
    setClientSecret("");
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Annuler votre abonnement ? Votre commerce restera actif jusqu'à la fin de la période payée.")) return;
    setLoading(true); setErr("");
    try {
      await api.post("/billing/cancel");
      await loadAll();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resumeSub = async () => {
    setLoading(true); setErr("");
    try {
      await api.post("/billing/resume");
      await loadAll();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const changePlan = async (newPlan) => {
    if (!window.confirm(`Passer au plan ${newPlan} ? La différence sera ajustée au prorata sur votre prochain cycle.`)) return;
    setLoading(true); setErr("");
    try {
      await api.post("/billing/change-plan", { plan_id: newPlan });
      await loadAll();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const resendVerification = async () => {
    setResendMsg("…");
    setDevVerifyToken("");
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.dev_verify_url) {
        // Extract just the token so we can offer a one-click verify button
        const tok = String(data.dev_verify_url).split("/").pop();
        setDevVerifyToken(tok);
        setResendMsg("");
      } else {
        setResendMsg("Courriel renvoyé. Vérifiez votre boîte de réception.");
      }
    } catch (e) {
      setResendMsg(formatApiError(e.response?.data?.detail));
    }
  };

  const verifyNow = async () => {
    if (!devVerifyToken) return;
    setVerifying(true);
    try {
      await api.post(`/auth/verify-email/${devVerifyToken}`);
      await refresh();
      setResendMsg("Courriel vérifié avec succès.");
      setDevVerifyToken("");
      await loadAll();
    } catch (e) {
      setResendMsg(formatApiError(e.response?.data?.detail));
    } finally {
      setVerifying(false);
    }
  };

  if (!user) return null;
  const verified = user.email_verified;
  const subStatus = status?.subscription_status;
  const isActive = subStatus === "active";
  const isPastDue = subStatus === "past_due";
  const currentPlan = status?.plan_id;
  const statusLabel = STATUS_LABEL[subStatus] || { fr: subStatus || "—", color: "text-[#A8A39D]" };

  return (
    <div className="p-8 lg:p-12" data-testid="billing-page">
      <div className="overline mb-2">Abonnement</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light mb-10">Plan & paiement</h1>

      {/* Status banner */}
      <div className="stat-card mb-8 flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
        <div className="flex-1">
          <div className="overline mb-2">Statut actuel</div>
          <div className={`font-serif text-3xl ${statusLabel.color}`} data-testid="billing-status">{statusLabel.fr}</div>
          {currentPlan && <div className="text-sm text-[#A8A39D] mt-1">Plan <span className="text-[#C5A059] uppercase tracking-widest text-xs">{currentPlan}</span></div>}
          {status?.next_renewal && !status?.cancel_at_period_end && (
            <div className="text-sm text-[#A8A39D] mt-2">Prochain renouvellement : <span className="text-[#F5F0EB]">{new Date(status.next_renewal).toLocaleDateString("fr-CA")}</span></div>
          )}
          {status?.cancel_at_period_end && (
            <div className="text-sm text-orange-400 mt-2">Annulation programmée — actif jusqu'au {status.next_renewal ? new Date(status.next_renewal).toLocaleDateString("fr-CA") : "—"}</div>
          )}
          {status?.grace_period_until && isPastDue && (
            <div className="text-sm text-orange-400 mt-2">Grâce jusqu'au {status.grace_period_until.slice(0, 10)}</div>
          )}
        </div>
        {isActive && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="open-portal" onClick={openPortal} disabled={loading} className="btn-ghost text-xs"><CreditCard size={14} /> Mettre à jour la carte</button>
            {!status?.cancel_at_period_end ? (
              <>
                {currentPlan === "standard" && <button data-testid="upgrade-premium" onClick={() => changePlan("premium")} disabled={loading} className="btn-ghost text-xs"><Star size={14} /> Passer Premium</button>}
                {currentPlan === "premium" && <button data-testid="downgrade-standard" onClick={() => changePlan("standard")} disabled={loading} className="btn-ghost text-xs">Passer Standard</button>}
                <button data-testid="cancel-sub" onClick={cancelSub} disabled={loading} className="px-5 py-2 text-xs tracking-widest uppercase border border-red-400/40 text-red-400 hover:bg-red-400/10 transition-colors flex items-center gap-2"><XCircle size={14} /> Annuler</button>
              </>
            ) : (
              <button data-testid="resume-sub" onClick={resumeSub} disabled={loading} className="btn-gold text-xs"><RotateCcw size={14} /> Reprendre</button>
            )}
          </div>
        )}
      </div>

      {!verified && (
        <div data-testid="verify-warning" className="border border-orange-400/40 bg-orange-400/5 p-6 mb-8 flex flex-col md:flex-row md:items-center gap-4">
          <AlertCircle className="text-orange-400 flex-shrink-0" size={24} />
          <div className="flex-1">
            <div className="font-serif text-xl">Confirmez votre adresse courriel</div>
            <div className="text-sm text-[#A8A39D] mt-1">Vous devez confirmer votre courriel avant de souscrire à un abonnement.</div>
            {resendMsg && <div className="text-xs text-[#C5A059] mt-3 break-all">{resendMsg}</div>}
            {devVerifyToken && (
              <div data-testid="dev-verify-block" className="mt-4 p-4 border border-[#C5A059]/30 bg-[#C5A059]/5 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-1">Mode développement</div>
                  <div className="text-xs text-[#A8A39D]">Resend non configuré : vérifiez votre compte d'un clic ci-dessous (en production, ce sera un courriel automatique).</div>
                </div>
                <button
                  data-testid="dev-verify-now"
                  onClick={verifyNow}
                  disabled={verifying}
                  className="btn-gold text-xs whitespace-nowrap"
                >
                  {verifying ? <><Loader2 className="animate-spin" size={14} /> Vérification…</> : <><Check size={14} /> Vérifier maintenant</>}
                </button>
              </div>
            )}
          </div>
          <button data-testid="resend-verification" onClick={resendVerification} className="btn-ghost"><Mail size={14} /> Renvoyer</button>
        </div>
      )}

      {pollMsg && (
        <div data-testid="poll-msg" className={`p-4 mb-8 border ${pollMsg.includes('confirmé') ? 'border-[#C5A059]/40 text-[#C5A059]' : 'border-white/10 text-[#A8A39D]'} flex items-center gap-3`}>
          {polling && <Loader2 className="animate-spin" size={16} />} {pollMsg}
        </div>
      )}

      {err && <div className="text-red-400 text-sm mb-6">{err}</div>}

      {!stripePromise && status?.subscription_status !== "active" && (
        <div data-testid="stripe-publishable-missing-top" className="border border-orange-400/30 bg-orange-400/5 p-4 mb-8 flex items-center gap-3">
          <AlertCircle className="text-orange-400 flex-shrink-0" size={20} />
          <div className="flex-1 text-xs text-[#A8A39D]">
            <span className="text-orange-400">Paiement temporairement indisponible —</span> la clé publique Stripe n'est pas configurée côté serveur.
          </div>
        </div>
      )}

      {selectedPlan && !stripePromise && (
        <div data-testid="stripe-config-missing" className="border border-orange-400/40 bg-orange-400/5 p-6 mb-8">
          <div className="font-serif text-2xl mb-2 text-orange-400">Configuration Stripe incomplète</div>
          <div className="text-sm text-[#A8A39D]">
            La clé publique Stripe (<code className="text-[#C5A059]">STRIPE_PUBLISHABLE_KEY</code>) n'est pas configurée
            dans les variables d'environnement. Contactez le support.
          </div>
        </div>
      )}

      {selectedPlan && stripePromise && (
        <div data-testid="embedded-checkout-container" className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Paiement sécurisé · Plan {selectedPlan}</div>
            <button
              data-testid="cancel-embedded-checkout"
              onClick={cancelEmbeddedCheckout}
              className="text-xs text-[#A8A39D] hover:text-[#F5F0EB] tracking-widest uppercase flex items-center gap-1"
            >
              <XCircle size={12} /> Annuler
            </button>
          </div>
          <div className="bg-white border border-[#C5A059]/30 rounded-sm p-4 md:p-6 shadow-[0_0_40px_rgba(197,160,89,0.15)]">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ fetchClientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
          <div className="mt-4 flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#A8A39D]">
            <Lock size={11} className="text-[#C5A059]" /> Paiement chiffré · Stripe · PCI-DSS · Annulable à tout moment
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((p) => {
          const isCurrent = currentPlan === p.id && isActive;
          const isPremium = p.id === "premium";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`luxe-card p-8 relative ${isPremium ? "border-[#C5A059]/40" : ""}`}>
              {isPremium && (
                <div className="absolute top-0 right-0 px-3 py-1 bg-[#C5A059] text-[#0A0908] text-[10px] tracking-[0.3em] uppercase flex items-center gap-1">
                  <Star size={10} /> Recommandé
                </div>
              )}
              <div className="overline mb-3">{p.name_fr}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-6xl text-[#C5A059]">{p.price_cad.toFixed(0)}</span>
                <span className="text-[#A8A39D] text-sm">$ CAD / mois</span>
              </div>
              <ul className="mt-8 space-y-3">
                {p.features_fr.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#F5F0EB]">
                    <Check size={16} className="text-[#C5A059] mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {isCurrent ? (
                  <div className="text-center py-3 border border-[#C5A059] text-[#C5A059] text-xs tracking-widest uppercase">Plan actuel</div>
                ) : (
                  <button data-testid={`subscribe-${p.id}`} onClick={() => checkout(p.id)} disabled={!verified || loading || polling} className="btn-gold w-full">
                    {loading ? <Loader2 className="animate-spin" size={14} /> : <>S'abonner <ArrowRight size={14} /></>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-xs text-[#A8A39D] max-w-2xl">
        Paiement sécurisé via Stripe. Annulation à tout moment. Période de grâce automatique de 6 jours en cas de paiement échoué — votre commerce reste en ligne pendant ce délai.
      </div>
    </div>
  );
}
